import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
} from "recharts";
import { FiTrendingUp, FiBarChart2 } from "react-icons/fi";
import { getAllSubscriptions } from "../../../../services/subscriptionServices";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const euro = (value) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const euroCompact = (value) =>
  new Intl.NumberFormat("fr-FR", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const monthFormatter = new Intl.DateTimeFormat("fr-FR", {
  month: "short",
  year: "2-digit",
});

const fullMonthFormatter = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
});

const SubscriptionsCharts = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getAllSubscriptions();

        const activeSubscriptions = (data || []).filter(
          (sub) => sub.status === "Abonné"
        );

        const revenueByMonth = {};

        activeSubscriptions.forEach((sub) => {
          if (!sub.start_date) return;

          const date = new Date(sub.start_date);
          if (Number.isNaN(date.getTime())) return;

          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;
          const amount = Number.parseFloat(sub.total_amount || 0) || 0;

          if (!revenueByMonth[monthKey]) {
            revenueByMonth[monthKey] = {
              monthKey,
              month: monthFormatter.format(date),
              monthLong: fullMonthFormatter.format(date),
              total: 0,
              subscriptions: 0,
            };
          }

          revenueByMonth[monthKey].total += amount;
          revenueByMonth[monthKey].subscriptions += 1;
        });

        const sortedData = Object.keys(revenueByMonth)
          .sort()
          .map((key) => revenueByMonth[key]);

        let cumulative = 0;
        const finalData = sortedData.map((item) => {
          cumulative += item.total;
          return {
            ...item,
            cumulative,
          };
        });

        setChartData(finalData);
      } catch (err) {
        console.error("Erreur lors de la récupération des abonnements :", err);
        setError("Impossible de charger les graphiques d’abonnements.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  const summary = useMemo(() => {
    const totalRevenue = chartData.reduce(
      (sum, item) => sum + Number(item.total || 0),
      0
    );

    const totalSubscriptions = chartData.reduce(
      (sum, item) => sum + Number(item.subscriptions || 0),
      0
    );

    const averageRevenue = chartData.length
      ? totalRevenue / chartData.length
      : 0;

    const lastMonth = chartData[chartData.length - 1] || null;

    return {
      totalRevenue,
      totalSubscriptions,
      averageRevenue,
      lastMonthRevenue: lastMonth?.total || 0,
      lastMonthLabel: lastMonth?.monthLong || "—",
    };
  }, [chartData]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {[1, 2].map((item) => (
          <div
            key={item}
            className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="h-6 w-48 animate-pulse rounded-lg bg-slate-100" />
            <div className="mt-3 h-4 w-64 animate-pulse rounded-lg bg-slate-100" />
            <div className="mt-6 h-[320px] animate-pulse rounded-2xl bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[28px] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* résumé */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          label="Chiffre d’affaires cumulé"
          value={euro(summary.totalRevenue)}
        />
        <SummaryCard
          label="Moyenne mensuelle"
          value={euro(summary.averageRevenue)}
        />
        <SummaryCard
          label={`Dernier mois (${summary.lastMonthLabel})`}
          value={euro(summary.lastMonthRevenue)}
        />
      </div>

      {/* graphiques */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartCard
          title="Chiffre d’affaires mensuel"
          subtitle="Montant des abonnements actifs par mois"
          icon={<FiBarChart2 />}
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <defs>
                <linearGradient id="barRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2EA7E0" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#123B57" stopOpacity={0.88} />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke="#E2E8F0"
              />

              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
                tickFormatter={euroCompact}
              />

              <Tooltip content={<RevenueTooltip />} cursor={{ fill: "#F8FAFC" }} />

              <Bar
                dataKey="total"
                name="CA mensuel"
                fill="url(#barRevenue)"
                radius={[10, 10, 4, 4]}
                barSize={34}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Revenu cumulé"
          subtitle="Progression cumulative du chiffre d’affaires"
          icon={<FiTrendingUp />}
        >
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <defs>
                <linearGradient id="lineFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2EA7E0" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#2EA7E0" stopOpacity={0.03} />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke="#E2E8F0"
              />

              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
                tickFormatter={euroCompact}
              />

              <Tooltip content={<CumulativeTooltip />} />

              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="#123B57"
                strokeWidth={3}
                dot={{ r: 4, fill: "#123B57", strokeWidth: 0 }}
                activeDot={{ r: 6, fill: "#2EA7E0", stroke: "#fff", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default SubscriptionsCharts;

/* ---------------------------------- */
/* UI */
/* ---------------------------------- */

function ChartCard({ title, subtitle, icon, children }) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-extrabold text-[#123B57]">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EEF7FC] text-lg text-[#123B57]">
          {icon}
        </div>
      </div>

      {children}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-extrabold tracking-tight text-[#123B57]">
        {value}
      </div>
    </div>
  );
}

function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
      <div className="text-sm font-bold text-[#123B57]">{data?.monthLong || label}</div>
      <div className="mt-2 text-sm text-slate-500">
        Chiffre d’affaires du mois
      </div>
      <div className="text-lg font-extrabold text-slate-900">
        {euro(data?.total || 0)}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Abonnements : {data?.subscriptions || 0}
      </div>
    </div>
  );
}

function CumulativeTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
      <div className="text-sm font-bold text-[#123B57]">{data?.monthLong || label}</div>
      <div className="mt-2 text-sm text-slate-500">Revenu cumulé</div>
      <div className="text-lg font-extrabold text-slate-900">
        {euro(data?.cumulative || 0)}
      </div>
    </div>
  );
}